import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import { type SortingState } from "@tanstack/react-table";

import { listFarmersTable, listFarmersTableReset } from "../../actions";
import { DEFAULT_SORTING, SEARCH_DEBOUNCE_MS, buildFarmersParams } from "./config";
import { type FarmerQuickFilterId } from "./quick-filters";
import { type Farmer } from "./types";

const NO_FARMERS: Farmer[] = [];

interface FarmersTableState {
  listFarmersTable: {
    data: { results?: Farmer[]; count?: number };
    isFetching: boolean;
    errorMessage: string;
  };
}

export const useFarmersTable = (aggregator?: string) => {
  const dispatch = useDispatch<Dispatch<UnknownAction>>();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [quickFilter, setQuickFilter] = useState<FarmerQuickFilterId | null>(null);

  const farmers = useSelector(
    (state: FarmersTableState) => state.listFarmersTable.data.results ?? NO_FARMERS
  );
  const count = useSelector((state: FarmersTableState) => state.listFarmersTable.data.count ?? 0);
  const isFetching = useSelector((state: FarmersTableState) => state.listFarmersTable.isFetching);
  const errorMessage = useSelector(
    (state: FarmersTableState) => state.listFarmersTable.errorMessage
  );

  // ponytail: the shared list reducer clears `data` on request, so the total is held across
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!isFetching) {
      setTotalCount(count);
    }
  }, [isFetching, count]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchTerm.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(
      listFarmersTable(
        buildFarmersParams({ aggregator, page, search, sorting, quickFilter })
      ) as unknown as UnknownAction
    );
  }, [dispatch, aggregator, page, search, sorting, quickFilter]);

  useEffect(() => {
    return () => {
      dispatch(listFarmersTableReset() as unknown as UnknownAction);
    };
  }, [dispatch]);

  const onSortingChange = (nextSorting: SortingState) => {
    setPage(1);
    setSorting(nextSorting);
  };

  const onQuickFilterChange = (nextQuickFilter: FarmerQuickFilterId | null) => {
    setPage(1);
    setQuickFilter(nextQuickFilter);
  };

  return {
    farmers,
    totalCount,
    isFetchingFarmers: isFetching,
    errorMessage,
    page,
    onPageChange: setPage,
    searchTerm,
    onSearchChange: setSearchTerm,
    sorting,
    onSortingChange,
    quickFilter,
    onQuickFilterChange,
  };
};
